<script lang="ts" setup>
import { routing } from '@/Support/Routing/Functions/routing'
import { t } from '@/Support/lang'
import SelectModal from '@/Components/App/Modals/SelectModal.vue'
import { ref, computed, useTemplateRef, nextTick } from 'vue'
import {
  SimpleItemListData,
  SimpleItemsListData,
} from '@/Support/Common/Interfaces/SimpleItemData'
import { RoutingParams } from '@/Support/Routing/Interfaces/routing'
import { tryFetchGet, tryFetchPut } from '@/Support/Routing/Functions/tryFetch'
import { useSelectTeamables } from '@/stores/selectTeamables'
import { useAlertModal } from '@/stores/alertModal'
import { AxiosError } from 'axios'
import { RelationshipAction } from '@/Support/Global/Enums/RelationshipAction'

const store = useSelectTeamables()

const routeParameters = computed(
  () =>
    store.routeParams || {
      model_type: store.modelType,
      model_id: store.modelId,
    },
)

const emit = defineEmits<{
  (e: 'selected', value: string): void
  (e: 'close'): void
}>()

const idsTeamablesSelected = ref<string[] | string | null>([])
const selectModal =
  useTemplateRef<InstanceType<typeof SelectModal>>('selectModal')
const openTeamables = ref(false)
const open = () => {
  idsTeamablesSelected.value = store.idsTeamablesSelected || []
  openTeamables.value = true
}

const isIdsTeamablesFilled = computed(
  () => store.idsTeamablesSelected !== false,
)

const teamableIdLoading = ref<string | null>(null)
const error = ref<string | null>(null)
const selected = async (teamable_id: string) => {
  error.value = null
  teamableIdLoading.value = teamable_id
  let action
  if (store.relationshipAction) {
    action = store.relationshipAction
  } else {
    if (idsTeamablesSelected.value === null) {
      idsTeamablesSelected.value = []
    }
    const isNew = !idsTeamablesSelected.value.includes(teamable_id)
    action = isNew ? 'create' : 'delete'
  }
  const teamable = teamablesLoaded.value?.data.filter(
    (item: SimpleItemListData) => item.id === teamable_id,
  )[0]

  if (teamable) {
    const _params = store.relationshipParents
      ? {
          parent_type: teamable.type,
          parent_id: teamable.id,
          model_type: store.modelType,
          model_id: store.modelId,
        }
      : {
          model_type: teamable.type,
          model_id: teamable.id,
          parent_type: store.modelType,
          parent_id: store.modelId,
        }
    const response = await tryFetchPut(
      routing('common-actions.relationship.item.store', _params),
      {
        on422: (_error: AxiosError) => {
          error.value = _error.response.data.message
          teamableIdLoading.value = null
        },
      },
      { action },
    )

    if (isIdsTeamablesFilled.value) {
      if (response) {
        idsTeamablesSelected.value = teamable_id
      } else {
        idsTeamablesSelected.value = store.idsTeamablesSelected
      }
    }

    if (store.onSelected) {
      store.onSelected()
    }
  }

  teamableIdLoading.value = null

  await loadTeamablesLinked()

  emit('selected', teamable_id)
}

const loading = ref(false)
const teamablesLoaded = ref<SimpleItemsListData | null>(null)
const idsTeamablesLoaded = computed(() =>
  teamablesLoaded.value?.data.map((item: SimpleItemListData) => item.id),
)
const itemsLoaded = async (items: SimpleItemsListData) => {
  teamablesLoaded.value = items

  await loadTeamablesLinked()
}

const loadTeamablesLinked = async () => {
  if (isIdsTeamablesFilled.value) {
    return
  }
  loading.value = true
  const response = await tryFetchGet(
    routing('common-actions.relationship.item.index', {
      model_type: store.modelType,
      model_id: store.modelId,
      item_ids: idsTeamablesLoaded.value,
      parents: store.relationshipParents,
    }),
  )
  if (response) {
    idsTeamablesSelected.value = response.data as string[]
  }
  loading.value = false
}

const close = () => {
  error.value = null
  emit('close')
  openTeamables.value = false
  if (store.onClose) {
    store.onClose()
  }
}

/**
 * Wide
 */

const wideClass = computed(() => (store.wide ? 'sm:max-w-md' : undefined))

defineExpose({
  open,
  close,
})
</script>
<template>
  <SelectModal
    ref="selectModal"
    :open="openTeamables"
    :icon="store.icon"
    route="teams.teamables.index"
    :route-params="routeParameters"
    :ids-selected="idsTeamablesSelected"
    :title="store.title"
    :description="store.description"
    :multiple="store.multiple"
    :id-loading="teamableIdLoading"
    :loading="loading"
    :show-name="store.showName"
    title-item-class="max-w-sm"
    :dialog-panel-class="wideClass"
    :error="error"
    :button-title="store.buttonTitle"
    @close="close"
    @selected="selected"
    @items-loaded="itemsLoaded"
  />
</template>
